import React, { useEffect, useState } from 'react';
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CircleCheckBig, CircleX, Clock, DollarSign, TrendingUp, TriangleAlert } from 'lucide-react';
import * as hr from '../../../api';
import { useHR } from '../../../HRContext';
import { fmtNumber } from '../../../format';
import { AChip, ngn, shortM } from './shared';
import { RequestDetail } from './Register';
import Loader from '../../../components/Loader';

// Request Dashboard (Stephanet's hrrc/adv/AdvDashboard.jsx): request volume, action status, types & urgency, financial summary, five charts
// and the overdue / emergency list.

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#e11d48', '#0891b2'];

function Card({ label, value, tone, Ico, badge, onClick, money }) {
  return (
    <button type="button" className={`adv-kpi t-${tone}`} onClick={onClick} disabled={!onClick}>
      <span className="adv-kpi-top"><small>{label}</small>{Ico && <Ico size={15} />}</span>
      <b>{money ? value : fmtNumber(value)}</b>
      {badge && <span className="adv-kpi-badge">{badge}</span>}
    </button>
  );
}

export default function AdvDashboard({ meta, onFilter }) {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [view, setView] = useState(null);
  const load = () => hr.advDashboard().then(setD).catch(e => notify(e.message, 'bad'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader title="Loading dashboard…" />;
  const k = d.kpis;
  const f = st => () => onFilter(st);
  const tip = { contentStyle: { fontSize: 12, borderRadius: 8 } };
  return (
    <div className="adv-dash">
      <p className="adv-group">Request Volume</p>
      <div className="adv-kpis">
        <Card label="Total Requests" value={k.total} tone="blue" Ico={TrendingUp} onClick={f(null)} />
        <Card label="Draft" value={k.draft} tone="gray" onClick={f('Draft')} />
        <Card label="Submitted" value={k.submitted} tone="sky" onClick={f('Submitted')} />
        <Card label="Under Review" value={k.under_review} tone="purple" onClick={f('Under Review')} />
        <Card label="Pending Approval" value={k.pending_approval} tone="yellow" Ico={Clock} onClick={f('Pending Approval')} />
        <Card label="Approved" value={k.approved} tone="green" Ico={CircleCheckBig} onClick={f('Approved')} />
        <Card label="Rejected" value={k.rejected} tone="red" Ico={CircleX} onClick={f('Rejected')} />
      </div>
      <p className="adv-group">Action Status</p>
      <div className="adv-kpis">
        <Card label="Returned for Correction" value={k.returned} tone="orange" onClick={f('Returned for Correction')} />
        <Card label="Closed" value={k.closed} tone="teal" onClick={f('Closed')} />
        <Card label="Cancelled" value={k.cancelled} tone="gray" onClick={f('Cancelled')} />
        <Card label="Assigned" value={k.assigned} tone="indigo" onClick={f('Approved')} />
        <Card label="In Progress" value={k.in_progress} tone="cyan" onClick={f('Approved')} />
        <Card label="Completed" value={k.completed} tone="emerald" onClick={f('Completed')} />
        <Card label="Overdue" value={k.overdue} tone="darkred" Ico={TriangleAlert} />
      </div>
      <p className="adv-group">Request Types and Urgency</p>
      <div className="adv-kpis">
        <Card label="Cost Requests" value={k.cost} tone="amber" />
        <Card label="Non-Cost Requests" value={k.non_cost} tone="slate" />
        <Card label="Procurement Requests" value={k.procurement} tone="violet" />
        <Card label="Payment Requests" value={k.payment} tone="rose" Ico={DollarSign} />
        <Card label="Emergency Requests" value={k.emergency} tone="darkred" badge="Emergency" />
        <Card label="Due Today" value={k.due_today} tone="yellow" Ico={Clock} />
        <Card label="Pending Finance" value={k.pending_finance} tone="orange" />
      </div>
      <p className="adv-group">Financial Summary</p>
      <div className="adv-kpis six">
        <Card money label="Total Requested" value={shortM(k.total_requested)} tone="blue" Ico={TrendingUp} />
        <Card money label="Total Approved" value={shortM(k.total_approved)} tone="green" Ico={CircleCheckBig} />
        <Card money label="Awaiting Payment Process" value={shortM(k.total_pending)} tone="orange" Ico={Clock} />
        <Card label="Pending Procurement" value={k.pending_procurement} tone="violet" />
        <Card label="Pending Management" value={k.pending_management} tone="red" Ico={TriangleAlert} onClick={f('Pending Approval')} />
        <Card label="Mixed Requests" value={k.mixed} tone="purple" />
      </div>
      <p className="hrc-sub">Payment stops at approval for now: an approved cost request needing payment waits as "Pending Finance" until the payment process is agreed.</p>

      <div className="adv-charts c2">
        <div className="mdr-card adv-chart"><h4>Requests by Category (Top 8)</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_category} margin={{ top: 4, right: 10, bottom: 36, left: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" interval={0} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip {...tip} />
              <Bar dataKey="val" name="Requests" radius={[3, 3, 0, 0]}>{d.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mdr-card adv-chart"><h4>Cost vs Non-Cost</h4>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart><Pie data={d.by_class} dataKey="val" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, val }) => `${name.replace(' Request', '')}: ${val}`} fontSize={10}>
              <Cell fill="#f59e0b" /><Cell fill="#06b6d4" /></Pie><Tooltip {...tip} /></PieChart>
          </ResponsiveContainer>
          <div className="adv-legend">{d.by_class.map((x, i) => <div key={x.name}><span>{x.name}</span><b style={{ color: i ? '#06b6d4' : '#f59e0b' }}>{x.val}</b></div>)}</div>
        </div>
      </div>
      <div className="adv-charts c3">
        <div className="mdr-card adv-chart"><h4>Requests by Department</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={d.by_dept} layout="vertical" margin={{ right: 20, left: 10 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} /><Tooltip {...tip} />
              <Bar dataKey="val" name="Requests" fill="#8b5cf6" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mdr-card adv-chart"><h4>Approval Status Breakdown</h4>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart><Pie data={d.by_status.filter(x => x.val > 0)} dataKey="val" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={66}>
              {d.by_status.filter(x => x.val > 0).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip {...tip} /></PieChart>
          </ResponsiveContainer>
          <div className="adv-statgrid">{d.by_status.filter(x => x.val > 0).map((x, i) => <div key={x.name}><b style={{ color: COLORS[i % COLORS.length] }}>{x.val}</b><small>{x.name}</small></div>)}
            {!d.by_status.some(x => x.val) && <p className="muted small">No requests yet</p>}</div>
        </div>
        <div className="mdr-card adv-chart"><h4>Requests by Month ({d.year})</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={d.by_month} margin={{ top: 5 }}>
              <XAxis dataKey="m" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip {...tip} />
              <Bar dataKey="val" name="Requests" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mdr-card mdr-table-card adv-attn">
        <div className="mds-table-head"><b><TriangleAlert size={14} /> Overdue and Emergency Requests</b><span className="mdr-chip t-red">{d.attention_count} requiring immediate attention</span></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table act-first">
            <thead><tr>{['Action', 'Ref', 'Date', 'Requested By', 'Category', 'Type', 'Priority', 'Status', 'Amount', 'Ageing'].map(x => <th key={x}>{x}</th>)}</tr></thead>
            <tbody>
              {d.attention.map(r => (
                <tr key={r.id} className="mdr-row">
                  <td><button className="mds-view" onClick={() => setView(r.id)}>View</button></td>
                  <td className="mono">{r.request_no}</td><td className="nowrap">{r.request_date}</td><td>{r.requested_by}</td>
                  <td className="small">{r.category}</td><td className="small">{r.request_type}</td><td><AChip text={r.priority} /></td><td><AChip text={r.status} /></td>
                  <td className="r nowrap">{r.total_requested ? ngn(r.total_requested) : '—'}</td>
                  <td className={r.ageing_days > 14 ? 'rd' : r.ageing_days > 7 ? 'am' : 'g'}>{r.ageing_days}d</td>
                </tr>
              ))}
              {!d.attention.length && <tr><td colSpan={10} className="mdr-empty">Nothing overdue or flagged emergency.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {view && <RequestDetail id={view} meta={meta} onClose={() => setView(null)} onChanged={load} />}
    </div>
  );
}
